import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from "react";

import Map, {
    Layer,
    NavigationControl,
    Popup,
    Source
} from "react-map-gl/maplibre";

import bbox from "@turf/bbox";

import {
    useNavigate
} from "react-router-dom";

import { DayPicker } from "react-day-picker";
import { es } from "date-fns/locale";
import "react-day-picker/dist/style.css";

import "maplibre-gl/dist/maplibre-gl.css";

import { useAuth } from "../../hooks/useAuth";
import {
    getToken,
    removeToken
} from "../../utils/TokenLS";
import {
    showAlert,
    SW_ICON
} from "../../utils/swAlert";

import {
    HTTP_VERBS,
    requestHttp
} from "../../utils/HttpRequest";

import {
    ActionButton,
    ErrorMessage,
    FilterContainer,
    FilterGroup,
    FilterLabel,
    FilterSelect,
    FloatingLegend,
    LegendColor,
    LegendContainer,
    LegendItem,
    LegendTitle,
    LoadingMessage,
    MapContainer,
    MapHeader,
    MapTitle,
    MapWrapper,
    PopupContent,
    PopupLabel,
    PopupRow,
    PopupTitle,
    StatisticsContainer,
    StatisticItem,
    StatisticLabel,
    StatisticValue,
    FarmsSplitContent,
    FarmsLeftPanel,
    FarmsRightPanel,
    FarmsTableTitle,
    FarmsDynamicTable,
    FarmNameCell,
    FarmCycleCell,
    FarmStatusBadge,
    DateRangeWrapper,
    DateRangeButton,
    DateRangeDropdown,
    DateRangeActions,
    FarmTransportCell
} from "./styles";

import { PageTitle } from "../../globalStyles"
import { Page } from "../../components/Page";

const EMPTY_GEOJSON = {
    type: "FeatureCollection",
    features: []
};

const parseJsonProperty = (value, fallback = {}) => {
    if (!value) {
        return fallback;
    }

    if (typeof value === "object") {
        return value;
    }

    try {
        return JSON.parse(value);
    } catch {
        return fallback;
    }
};

const parseArrayProperty = (value) => {
    if (Array.isArray(value)) {
        return value;
    }

    if (!value) {
        return [];
    }

    try {
        const parsedValue = JSON.parse(value);

        return Array.isArray(parsedValue)
            ? parsedValue
            : [];
    } catch {
        return [];
    }
};

const normalizeFarmProperties = (properties = {}) => {
    return {
        ...properties,

        intOrden:
            Number(properties.intOrden) || 999999,

        totalLots:
            Number(properties.totalLots) || 0,

        lotsWithGeometry:
            Number(properties.lotsWithGeometry) || 0,

        totalNetArea:
            Number(properties.totalNetArea) || 0,

        totalGrossArea:
            Number(properties.totalGrossArea) || 0,

        cycles:
            parseArrayProperty(properties.cycles),

        cyclesCount:
            Number(properties.cyclesCount) || 0,

        lastCycle:
            Number(properties.lastCycle) || 0,

        lastCycleConceptCode:
            properties.lastCycleConceptCode ?? "",

        lastCycleConceptLabel:
            properties.lastCycleConceptLabel ?? "",

        lastCycleMixtureName:
            properties.lastCycleMixtureName ?? "",

        lastCycleColor:
            properties.lastCycleColor ?? "#d1d5db",

        lastCycleBultosPerHectare:
            Number(properties.lastCycleBultosPerHectare) || 0,

        expectedLastCycleQuantity:
            Number(properties.expectedLastCycleQuantity) || 0,

        selectedCycle:
            properties.selectedCycle ?? "",

        selectedConceptCode:
            properties.selectedConceptCode ?? "",

        appliedLots:
            Number(properties.appliedLots) || 0,

        pendingLots:
            Number(properties.pendingLots) || 0,

        coveragePercentage:
            Number(properties.coveragePercentage) || 0,

        totalAppliedQuantity:
            Number(properties.totalAppliedQuantity) || 0,

        firstApplicationDate:
            properties.firstApplicationDate ?? null,

        lastApplicationDate:
            properties.lastApplicationDate ?? null,

        lastCycleWorkersCount:
            Number(properties.lastCycleWorkersCount) || 0,

        applicationDaysCount:
            Number(properties.applicationDaysCount) || 0,

        workerDaysCount:
            Number(properties.workerDaysCount) || 0,

        appliedBagsPerWorkerDay:
            Number(properties.appliedBagsPerWorkerDay) || 0,

        daysSinceFirstApplication:
            properties.daysSinceFirstApplication === null ||
                properties.daysSinceFirstApplication === undefined ||
                properties.daysSinceFirstApplication === ""
                ? null
                : Number(properties.daysSinceFirstApplication),

        daysSinceLastApplication:
            properties.daysSinceLastApplication === null ||
                properties.daysSinceLastApplication === undefined ||
                properties.daysSinceLastApplication === ""
                ? null
                : Number(properties.daysSinceLastApplication),

        totalsByConcept:
            parseJsonProperty(
                properties.totalsByConcept,
                {}
            ),

        pendingLastCycleQuantity:
            Number(properties.pendingLastCycleQuantity) || 0,

        progressLastCyclePercentage:
            Number(properties.progressLastCyclePercentage) || 0,

        firstReceptionDate:
            properties.firstReceptionDate ?? null,

        receivedLastCycleQuantity:
            Number(properties.receivedLastCycleQuantity) || 0,

        receptionCount:
            Number(properties.receptionCount) || 0,

        pendingReceptionQuantity:
            Number(properties.pendingReceptionQuantity) || 0,

        pendingApplyFromReceivedQuantity:
            Number(properties.pendingApplyFromReceivedQuantity) || 0,

        receptionProgressPercentage:
            Number(properties.receptionProgressPercentage) || 0,

        remisiones:
            parseArrayProperty(properties.remisiones),

        suppliers:
            parseArrayProperty(properties.suppliers),

        firstTransportDate:
            properties.firstTransportDate ?? null,

        lastTransportDate:
            properties.lastTransportDate ?? null,

        transportTripsCount:
            Number(properties.transportTripsCount) || 0,

        transportedPeopleQuantity:
            Number(properties.transportedPeopleQuantity) || 0,

        transportDaysCount:
            Number(properties.transportDaysCount) || 0,

        transportedPeoplePerTrip:
            Number(properties.transportedPeoplePerTrip) || 0,

        transportVsWorkerDaysDiff:
            Number(properties.transportVsWorkerDaysDiff) || 0,

        transportPlates:
            parseArrayProperty(properties.transportPlates),

        transportDrivers:
            parseArrayProperty(properties.transportDrivers),

        transportOrigins:
            parseArrayProperty(properties.transportOrigins),

        hasTransportToday:
            Boolean(properties.hasTransportToday),

        todayTransportDate:
            properties.todayTransportDate ?? null,

        todayTransportTripsCount:
            Number(properties.todayTransportTripsCount) || 0,

        todayTransportedPeopleQuantity:
            Number(properties.todayTransportedPeopleQuantity) || 0,

        todayTransportCycles:
            parseArrayProperty(properties.todayTransportCycles),

        todayTransportPlates:
            parseArrayProperty(properties.todayTransportPlates),

        todayTransportDrivers:
            parseArrayProperty(properties.todayTransportDrivers),
    };
};

const getLastConceptCodeByFarm = (farmProperties = {}) => {
    const totalsByConcept =
        farmProperties.totalsByConcept ?? {};

    const conceptEntries =
        Object.entries(totalsByConcept)
            .map(([conceptCode, concept]) => {
                const cycles =
                    Array.isArray(concept?.cycles)
                        ? concept.cycles
                        : [];

                const validCycles =
                    cycles
                        .map(Number)
                        .filter(
                            (cycleNumber) =>
                                Number.isInteger(
                                    cycleNumber
                                ) &&
                                cycleNumber > 0
                        );

                const maxCycle =
                    validCycles.length > 0
                        ? Math.max(...validCycles)
                        : 0;

                return {
                    conceptCode,
                    maxCycle,
                    totalQuantity:
                        Number(
                            concept?.totalQuantity
                        ) || 0,
                    applicationCount:
                        Number(
                            concept?.applicationCount
                        ) || 0
                };
            })
            .filter(
                (concept) =>
                    concept.maxCycle > 0 ||
                    concept.totalQuantity > 0 ||
                    concept.applicationCount > 0
            );

    if (!conceptEntries.length) {
        return "";
    }

    conceptEntries.sort((a, b) => {
        if (b.maxCycle !== a.maxCycle) {
            return b.maxCycle - a.maxCycle;
        }

        if (b.totalQuantity !== a.totalQuantity) {
            return b.totalQuantity - a.totalQuantity;
        }

        return b.applicationCount - a.applicationCount;
    });

    return conceptEntries[0].conceptCode;
};

const buildFarmDetailUrl = (farmProperties = {}) => {
    const farmCode =
        farmProperties.farmCode;

    if (!farmCode) {
        return "";
    }

    const params = new URLSearchParams();

    if (farmProperties.lastCycle) {
        params.set(
            "cycle",
            String(farmProperties.lastCycle)
        );
    }

    const lastConceptCode =
        getLastConceptCodeByFarm(
            farmProperties
        );

    if (lastConceptCode) {
        params.set(
            "conceptCode",
            lastConceptCode
        );
    }

    const queryString =
        params.toString();

    return queryString
        ? `/fertilization/farms/${farmCode}?${queryString}`
        : `/fertilization/farms/${farmCode}`;
};

const formatDate = (dateValue) => {
    if (!dateValue) {
        return "Sin registro";
    }

    const value = String(dateValue).trim();

    if (!value) {
        return "Sin registro";
    }

    // Si viene como YYYY-MM-DD
    const onlyDateMatch = value.match(
        /^(\d{4})-(\d{2})-(\d{2})/
    );

    if (onlyDateMatch) {
        const [, year, month, day] =
            onlyDateMatch;

        return `${day}/${month}/${year}`;
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return "Sin registro";
    }

    return new Intl.DateTimeFormat("es-CO", {
        timeZone: "America/Bogota",
        year: "numeric",
        month: "2-digit",
        day: "2-digit"
    }).format(date);
};

const formatNumber = (value) => {
    return Number(value ?? 0).toLocaleString("es-CO");
};

const isTokenError = (error) => {
    const status =
        error?.response?.status;

    return status === 401 || status === 403;
};

export const FarmsMap = () => {

    const sessionClosingRef = useRef(false);
    const mapRef = useRef(null);
    const dateRangeRef = useRef(null);

    const { auth, closeSeccionAuth } = useAuth();
    const navigate = useNavigate();

    const [year, setYear] = useState(new Date().getFullYear());

    const [cycle, setCycle] = useState("");

    const [cycleOptions, setCycleOptions] = useState([]);

    const [conceptCode, setConceptCode] = useState("");

    const [dateFrom, setDateFrom] = useState("");

    const [dateTo, setDateTo] = useState("");

    const [showDateRangePicker, setShowDateRangePicker] = useState(false);

    const [selectedDateRange, setSelectedDateRange] = useState({
        from: undefined,
        to: undefined
    });

    const [geojson, setGeojson] =
        useState(EMPTY_GEOJSON);

    const [statistics, setStatistics] =
        useState(null);

    const [hoveredFarm, setHoveredFarm] =
        useState(null);

    const [loading, setLoading] =
        useState(false);

    const [error, setError] =
        useState("");

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                dateRangeRef.current &&
                !dateRangeRef.current.contains(event.target)
            ) {
                setShowDateRangePicker(false);
            }
        };

        document.addEventListener(
            "mousedown",
            handleClickOutside
        );

        return () => {
            document.removeEventListener(
                "mousedown",
                handleClickOutside
            );
        };
    }, []);

    const handleCloseSession = useCallback(() => {
        if (sessionClosingRef.current) {
            return;
        }

        sessionClosingRef.current = true;

        closeSeccionAuth();
        removeToken();

        showAlert(
            "Inicie sesión nuevamente",
            "Su token puede estar vencido",
            SW_ICON.INFO,
            () => {
                navigate("/Login", {
                    replace: true
                });
            }
        );
    }, [
        closeSeccionAuth,
        navigate
    ]);

    const updateCycleOptionsFromGeojson = useCallback((geojsonData) => {
        const features =
            geojsonData?.features ?? [];

        const cycles = features.flatMap((feature) => {
            const farm = normalizeFarmProperties(
                feature.properties ?? {}
            );

            return farm.cycles ?? [];
        });

        const uniqueCycles = [
            ...new Set(
                cycles
                    .map(Number)
                    .filter(
                        (cycleNumber) =>
                            Number.isInteger(cycleNumber) &&
                            cycleNumber > 0
                    )
            )
        ].sort((a, b) => a - b);

        setCycleOptions(uniqueCycles);
    }, []);

    const loadFarms = useCallback(
        async () => {
            const token = getToken();

            if (!token) {
                handleCloseSession();
                return;
            }
            setLoading(true);
            setError("");

            try {
                const response =
                    await requestHttp({
                        method:
                            HTTP_VERBS.GET,

                        endpoint: "/api/farms-map",
                        params: {
                            year,
                            ...(cycle ? { cycle } : {}),
                            ...(conceptCode ? { conceptCode } : {}),
                            ...(dateFrom ? { dateFrom } : {}),
                            ...(dateTo ? { dateTo } : {})
                        }
                    });

                const result =
                    response?.data?.data;

                //console.log({ result: result })

                const mapGeojson =
                    result?.geojson ??
                    EMPTY_GEOJSON;

                setGeojson(mapGeojson);

                if (!cycle) {
                    updateCycleOptionsFromGeojson(
                        mapGeojson
                    );
                }

                setStatistics(
                    result?.statistics ??
                    null
                );

                window.requestAnimationFrame(
                    () => {
                        if (
                            !mapRef.current ||
                            !mapGeojson.features
                                .length
                        ) {
                            return;
                        }

                        const [
                            west,
                            south,
                            east,
                            north
                        ] = bbox(mapGeojson);

                        mapRef.current.fitBounds(
                            [
                                [
                                    west,
                                    south
                                ],
                                [
                                    east,
                                    north
                                ]
                            ],
                            {
                                padding: 80,
                                duration: 700,
                                maxZoom: 13
                            }
                        );
                    }
                );
            } catch (requestError) {
                console.error(
                    requestError
                );

                if (isTokenError(requestError)) {
                    handleCloseSession();
                    return;
                }

                setError(
                    requestError
                        ?.response?.data
                        ?.message ||
                    requestError?.message ||
                    "No fue posible cargar las fincas"
                );
            } finally {
                setLoading(false);
            }
        },
        [
            year,
            cycle,
            conceptCode,
            dateFrom,
            dateTo,
            updateCycleOptionsFromGeojson,
            handleCloseSession
        ]
    );

    useEffect(() => {
        const getData = async () => {
            const token = getToken();

            if (!token) {
                handleCloseSession();
                return;
            }
            await loadFarms();
        };

        getData();
    }, [
        auth,
        loadFarms,
        handleCloseSession
    ]);

    const goToFarmDetail = useCallback(
        (farm) => {
            const farmCode =
                farm?.farmCode;

            if (!farmCode) {
                return;
            }

            const params = new URLSearchParams();

            const selectedCycle =
                cycle || farm.lastCycle;

            if (selectedCycle) {
                params.set(
                    "cycle",
                    String(selectedCycle)
                );
            }

            const selectedConceptCode =
                conceptCode ||
                farm.lastCycleConceptCode ||
                getLastConceptCodeByFarm(farm);

            if (selectedConceptCode) {
                params.set(
                    "conceptCode",
                    selectedConceptCode
                );
            }

            if (dateFrom) {
                params.set(
                    "dateFrom",
                    dateFrom
                );
            }

            if (dateTo) {
                params.set(
                    "dateTo",
                    dateTo
                );
            }

            const queryString =
                params.toString();

            const url =
                queryString
                    ? `/fertilization/farms/${farmCode}?${queryString}`
                    : `/fertilization/farms/${farmCode}`;

            window.open(
                url,
                "_blank",
                "noopener,noreferrer"
            );
        },
        [
            cycle,
            conceptCode,
            dateFrom,
            dateTo
        ]
    );

    const farmCircleLayer = useMemo(
        () => ({
            id: "farms-circle",
            type: "circle",

            paint: {
                "circle-radius": [
                    "interpolate",
                    ["linear"],
                    ["zoom"],
                    5,
                    8,
                    10,
                    15,
                    15,
                    22
                ],

                "circle-color": [
                    "coalesce",
                    ["get", "statusColor"],
                    "#9ca3af"
                ],

                "circle-stroke-color":
                    "#ffffff",

                "circle-stroke-width":
                    3,

                "circle-opacity":
                    0.92
            }
        }),
        []
    );
    const farmLabelLayer = useMemo(
        () => ({
            id: "farms-label",
            type: "symbol",

            layout: {
                "text-field": [
                    "coalesce",
                    ["get", "farmName"],
                    ["get", "farmCode"],
                    ""
                ],

                "text-size": 13,
                "text-offset": [
                    0,
                    1.8
                ],

                "text-anchor": "top",
                "text-allow-overlap":
                    false
            },

            paint: {
                "text-color":
                    "#111827",

                "text-halo-color":
                    "#ffffff",

                "text-halo-width":
                    2
            }
        }),
        []
    );

    const handleMouseMove = useCallback(
        (event) => {
            const feature =
                event.features?.[0];

            const map =
                mapRef.current?.getMap?.();

            if (!feature) {
                setHoveredFarm(null);

                if (map) {
                    map.getCanvas().style.cursor =
                        "";
                }

                return;
            }

            if (map) {
                map.getCanvas().style.cursor =
                    "pointer";
            }

            setHoveredFarm({
                longitude:
                    feature.geometry.coordinates[0],

                latitude:
                    feature.geometry.coordinates[1],

                properties:
                    normalizeFarmProperties(
                        feature.properties
                    )
            });
        },
        []
    );

    const handleMapClick = useCallback(
        (event) => {
            const feature =
                event.features?.[0];

            if (!feature) {
                return;
            }

            const farm =
                normalizeFarmProperties(
                    feature.properties
                );

            goToFarmDetail(farm);
        },
        [goToFarmDetail]
    );
    //Para Tabla
    const farmsTableData = useMemo(() => {
        const features =
            geojson?.features ?? [];

        return features
            .map((feature) =>
                normalizeFarmProperties(
                    feature.properties ?? {}
                )
            )
            .sort((a, b) => {
                const orderA =
                    Number(a.intOrden) || 999999;

                const orderB =
                    Number(b.intOrden) || 999999;

                if (orderA !== orderB) {
                    return orderA - orderB;
                }

                return String(a.farmName ?? "")
                    .localeCompare(
                        String(b.farmName ?? ""),
                        "es"
                    );
            });
    }, [geojson]);

    const tableCycles = useMemo(() => {
        const cycles =
            farmsTableData.flatMap(
                (farm) => farm.cycles ?? []
            );

        return [
            ...new Set(
                cycles
                    .map(Number)
                    .filter(
                        (cycleNumber) =>
                            Number.isInteger(cycleNumber) &&
                            cycleNumber > 0
                    )
            )
        ].sort((a, b) => a - b);
    }, [farmsTableData]);

    const getFarmCycleColor = ({
        farm,
        cycleNumber
    }) => {
        const hasCycle =
            farm.cycles
                ?.map(Number)
                .includes(Number(cycleNumber));

        if (!hasCycle) {
            return "#f3f4f6";
        }

        if (
            Number(farm.lastCycle) ===
            Number(cycleNumber)
        ) {
            return farm.lastCycleColor || farm.statusColor || "#f0a808";
        }

        return "#dbeafe";
    };

    const farmsTableTotals = useMemo(() => {
        const totals = farmsTableData.reduce(
            (accumulator, farm) => {
                accumulator.totalNetArea +=
                    Number(farm.totalNetArea) || 0;

                accumulator.totalLots +=
                    Number(farm.totalLots) || 0;

                accumulator.expectedLastCycleQuantity +=
                    Number(farm.expectedLastCycleQuantity) || 0;

                accumulator.receivedLastCycleQuantity +=
                    Number(farm.receivedLastCycleQuantity) || 0;

                accumulator.totalAppliedQuantity +=
                    Number(farm.totalAppliedQuantity) || 0;

                accumulator.pendingApplyFromReceivedQuantity +=
                    Number(farm.pendingApplyFromReceivedQuantity) || 0;

                accumulator.transportTripsCount +=
                    Number(farm.transportTripsCount) || 0;

                accumulator.applicationDaysCount +=
                    Number(farm.applicationDaysCount) || 0;

                accumulator.transportedPeopleQuantity +=
                    Number(farm.transportedPeopleQuantity) || 0;

                accumulator.workerDaysCount +=
                    Number(farm.workerDaysCount) || 0;

                accumulator.pendingReceptionQuantity +=
                    Number(farm.pendingReceptionQuantity) || 0;

                return accumulator;
            },
            {
                totalNetArea: 0,
                totalLots: 0,
                expectedLastCycleQuantity: 0,
                receivedLastCycleQuantity: 0,
                totalAppliedQuantity: 0,
                pendingApplyFromReceivedQuantity: 0,
                transportTripsCount: 0,
                applicationDaysCount: 0,
                transportedPeopleQuantity: 0,
                workerDaysCount: 0,
                pendingReceptionQuantity: 0,
                averageBultosPerHectare: 0,
                averageBagsPerWorkerDay: 0
            }
        );

        totals.averageBultosPerHectare =
            totals.totalNetArea > 0
                ? Math.round(
                    totals.expectedLastCycleQuantity /
                    totals.totalNetArea
                )
                : 0;

        totals.averageBagsPerWorkerDay =
            totals.workerDaysCount > 0
                ? Math.round(
                    totals.totalAppliedQuantity /
                    totals.workerDaysCount
                )
                : 0;

        return totals;
    }, [farmsTableData]);

    const formatDateToInput = (dateValue) => {
        if (!dateValue) return "";

        const year = dateValue.getFullYear();
        const month = String(dateValue.getMonth() + 1).padStart(2, "0");
        const day = String(dateValue.getDate()).padStart(2, "0");

        return `${year}-${month}-${day}`;
    };

    const formatDateRangeLabel = () => {
        if (!selectedDateRange?.from && !selectedDateRange?.to) {
            return "Seleccione rango de fechas";
        }

        if (selectedDateRange?.from && !selectedDateRange?.to) {
            return `${formatDate(selectedDateRange.from)} - Seleccione fecha final`;
        }

        return `${formatDate(selectedDateRange.from)} - ${formatDate(selectedDateRange.to)}`;
    };

    const handleDateRangeSelect = (range) => {
        setSelectedDateRange(
            range ?? {
                from: undefined,
                to: undefined
            }
        );

        setCycle("");

        setDateFrom(
            range?.from
                ? formatDateToInput(range.from)
                : ""
        );

        setDateTo(
            range?.to
                ? formatDateToInput(range.to)
                : ""
        );
    };

    const clearDateRange = () => {
        setYear(new Date().getFullYear());
        setCycle("");
        setConceptCode("");

        setDateFrom("");
        setDateTo("");

        setSelectedDateRange({
            from: undefined,
            to: undefined
        });

        setShowDateRangePicker(false);
    };

    const clearFilters = () => {
        setYear(new Date().getFullYear());
        setCycle("");
        setConceptCode("");

        setDateFrom("");
        setDateTo("");

        setSelectedDateRange({
            from: undefined,
            to: undefined
        });

        setShowDateRangePicker(false);
    };

    // cycleOptions se carga una sola vez desde la consulta sin ciclo.
    // Al cambiar de ciclo, loadFarms vuelve a consultar el backend,
    // pero esta lista no se recalcula desde el geojson filtrado.



    return (
        <Page>
            <PageTitle>
                CICLOS DE FERTILIZACIÓN
            </PageTitle>

            <FilterContainer>
                <FilterGroup>
                    <FilterLabel>
                        Año
                    </FilterLabel>

                    <FilterSelect
                        value={year}
                        onChange={(event) => {
                            setYear(
                                Number(event.target.value)
                            );
                            setCycle("");
                        }}
                    >
                        {[
                            new Date().getFullYear() - 2,
                            new Date().getFullYear() - 1,
                            new Date().getFullYear(),
                            new Date().getFullYear() + 1
                        ].map((yearOption) => (
                            <option
                                key={yearOption}
                                value={yearOption}
                            >
                                {yearOption}
                            </option>
                        ))}
                    </FilterSelect>
                </FilterGroup>

                <FilterGroup>
                    <FilterLabel>
                        Ciclo
                    </FilterLabel>

                    <FilterSelect
                        value={cycle}
                        onChange={(event) =>
                            setCycle(event.target.value)
                        }
                    >
                        <option value="">
                            Todos
                        </option>

                        {cycleOptions.map((cycleOption) => (
                            <option
                                key={cycleOption}
                                value={cycleOption}
                            >
                                Ciclo {cycleOption}
                            </option>
                        ))}
                    </FilterSelect>
                </FilterGroup>

                <FilterGroup>
                    <FilterLabel>
                        Tipo fertilización
                    </FilterLabel>

                    <FilterSelect
                        value={conceptCode}
                        onChange={(event) => {
                            setConceptCode(event.target.value);
                            setCycle("");
                        }}
                    >
                        <option value="">
                            Todas
                        </option>
                        <option value="112">
                            Mezcla
                        </option>
                        <option value="228">
                            Enmienda
                        </option>
                    </FilterSelect>
                </FilterGroup>

                <FilterGroup>
                    <FilterLabel>
                        Rango de fechas
                    </FilterLabel>

                    <DateRangeWrapper ref={dateRangeRef}>
                        <DateRangeButton
                            type="button"
                            onClick={() =>
                                setShowDateRangePicker(
                                    (currentValue) => !currentValue
                                )
                            }
                        >
                            {formatDateRangeLabel()}
                        </DateRangeButton>

                        {showDateRangePicker && (
                            <DateRangeDropdown>
                                <DayPicker
                                    mode="range"
                                    selected={selectedDateRange}
                                    onSelect={handleDateRangeSelect}
                                    locale={es}
                                    numberOfMonths={2}
                                    defaultMonth={
                                        selectedDateRange?.from ||
                                        new Date()
                                    }
                                    showWeekNumber
                                    ISOWeek
                                    fromDate={new Date(Number(year), 0, 1)}
                                    toDate={new Date(Number(year), 11, 31)}
                                />

                                <DateRangeActions>
                                    <ActionButton
                                        type="button"
                                        onClick={clearFilters}
                                    >
                                        Limpiar
                                    </ActionButton>

                                    <ActionButton
                                        type="button"
                                        onClick={() =>
                                            setShowDateRangePicker(false)
                                        }
                                    >
                                        Aplicar
                                    </ActionButton>
                                </DateRangeActions>
                            </DateRangeDropdown>
                        )}
                    </DateRangeWrapper>
                </FilterGroup>

                <ActionButton
                    type="button"
                    onClick={loadFarms}
                    disabled={loading}
                >
                    Consultar
                </ActionButton>

                <ActionButton
                    type="button"
                    onClick={clearFilters}
                >
                    Limpiar filtros
                </ActionButton>

            </FilterContainer>

            {statistics && (
                <StatisticsContainer>
                    <StatisticItem>
                        <StatisticLabel>
                            Fincas
                        </StatisticLabel>

                        <StatisticValue>
                            {statistics.totalFarms ?? 0}
                        </StatisticValue>
                    </StatisticItem>

                    <StatisticItem>
                        <StatisticLabel>
                            Lotes activos
                        </StatisticLabel>

                        <StatisticValue>
                            {statistics.totalLots ?? 0}
                        </StatisticValue>
                    </StatisticItem>

                    <StatisticItem>
                        <StatisticLabel>
                            Área neta
                        </StatisticLabel>

                        <StatisticValue>
                            {formatNumber(
                                statistics.totalNetArea
                            )}
                        </StatisticValue>

                        <small>
                            ha
                        </small>
                    </StatisticItem>

                    <StatisticItem>
                        <StatisticLabel>
                            Fincas completas
                        </StatisticLabel>

                        <StatisticValue>
                            {statistics.farmsCompleted ?? 0}
                        </StatisticValue>
                    </StatisticItem>

                    <StatisticItem>
                        <StatisticLabel>
                            En proceso
                        </StatisticLabel>

                        <StatisticValue>
                            {statistics.farmsInProgress ?? 0}
                        </StatisticValue>
                    </StatisticItem>

                    <StatisticItem>
                        <StatisticLabel>
                            Sin aplicación
                        </StatisticLabel>

                        <StatisticValue>
                            {statistics.farmsWithoutApplication ?? 0}
                        </StatisticValue>
                    </StatisticItem>

                </StatisticsContainer>
            )}

            {loading && (
                <div>
                    Cargando fincas...
                </div>
            )}

            {error && (
                <div>
                    {error}
                </div>
            )}

            <FarmsSplitContent>
                <FarmsLeftPanel>
                    <FarmsTableTitle>
                        Resumen general por finca y ciclos
                        {cycle
                            ? ` - Ciclo ${cycle}`
                            : ""}
                        {conceptCode === "112"
                            ? " - Mezcla"
                            : conceptCode === "228"
                                ? " - Enmienda"
                                : ""}
                        {dateFrom || dateTo
                            ? ` - ${dateFrom || "inicio"} a ${dateTo || "fin"}`
                            : ""}
                    </FarmsTableTitle>

                    <FarmsDynamicTable>
                        <thead>
                            <tr>
                                <th>Finca</th>
                                <th>Área neta</th>
                                <th>Lotes</th>
                                <th>{cycle ? "Ciclo analizado" : "Último ciclo"}</th>
                                <th>Producto</th>
                                <th>Bu/Ha</th>
                                <th>Esperado</th>
                                <th>Recibido</th>
                                <th>Aplicado</th>
                                <th>En Bodega</th>
                                <th>Viajes</th>
                                <th>Días fert</th>
                                <th>Pers transp</th>
                                <th>Pers RDT</th>
                                <th>Bu/Pe</th>
                                <th>F_Recepción</th>
                                <th>1a_Aplicación</th>
                                <th>Últ_Aplicación</th>
                                <th>Días</th>

                                {tableCycles.map((cycleNumber) => (
                                    <th key={cycleNumber}>
                                        C{cycleNumber}
                                    </th>
                                ))}

                                <th>Cobertura</th>
                                <th>Estado</th>
                                <th>Pend x Recibir Prov</th>

                            </tr>
                        </thead>

                        <tbody>
                            {farmsTableData.map((farm) => (
                                <tr
                                    key={farm.farmCode}
                                    onClick={() =>
                                        goToFarmDetail(farm)
                                    }
                                >
                                    <FarmNameCell>
                                        {farm.farmName ||
                                            farm.farmCode}
                                    </FarmNameCell>

                                    <td>
                                        {formatNumber(
                                            farm.totalNetArea
                                        )}{" "}
                                        ha
                                    </td>

                                    <td>
                                        {farm.totalLots}
                                    </td>

                                    <FarmCycleCell
                                        $color={farm.lastCycleColor || "#f3f4f6"}
                                        $textColor="#ffffff"
                                    >
                                        {farm.lastCycle || "—"}
                                    </FarmCycleCell>

                                    <td>
                                        {farm.lastCycleMixtureName ||
                                            farm.lastCycleConceptLabel ||
                                            "—"}
                                    </td>
                                    <td>
                                        {farm.lastCycleBultosPerHectare || ""}
                                    </td>

                                    <td>                                        
                                        {formatNumber(farm.expectedLastCycleQuantity)}
                                    </td>
                                    <td>
                                        {formatNumber(
                                            farm.receivedLastCycleQuantity
                                        )}
                                    </td>
                                    <td>
                                        {formatNumber(farm.totalAppliedQuantity)}
                                    </td>
                                    <td>
                                        {formatNumber(
                                            farm.pendingApplyFromReceivedQuantity
                                        )}
                                    </td>
                                    <FarmTransportCell
                                        $active={farm.hasTransportToday}
                                        title={
                                            farm.hasTransportToday
                                                ? `Hoy hay transporte: ${farm.todayTransportTripsCount} viaje(s), ${farm.todayTransportedPeopleQuantity} persona(s)`
                                                : "Sin transporte registrado hoy"
                                        }
                                    >
                                        {farm.transportTripsCount || "—"}
                                    </FarmTransportCell>
                                    <td>
                                        {farm.applicationDaysCount || "—"}
                                    </td>
                                    <td>
                                        {farm.transportedPeopleQuantity || "—"}
                                    </td>

                                    <td>
                                        {farm.workerDaysCount || "—"}
                                    </td>

                                    <td>
                                        {farm.appliedBagsPerWorkerDay
                                            ? formatNumber(
                                                farm.appliedBagsPerWorkerDay
                                            )
                                            : "—"}
                                    </td>

                                    <td>
                                        {formatDate(
                                            farm.firstReceptionDate
                                        )}
                                    </td>
                                    <td>
                                        {formatDate(
                                            farm.firstApplicationDate
                                        )}
                                    </td>

                                    <td>
                                        {formatDate(
                                            farm.lastApplicationDate
                                        )}
                                    </td>

                                    <td>
                                        {farm.daysSinceFirstApplication ?? "—"}
                                    </td>

                                    {tableCycles.map((cycleNumber) => {
                                        const hasCycle =
                                            farm.cycles
                                                ?.map(Number)
                                                .includes(
                                                    Number(cycleNumber)
                                                );

                                        return (
                                            <FarmCycleCell
                                                key={`${farm.farmCode}-${cycleNumber}`}
                                                $color={getFarmCycleColor({
                                                    farm,
                                                    cycleNumber
                                                })}
                                            >
                                                {hasCycle ? "✓" : "—"}
                                            </FarmCycleCell>
                                        );
                                    })}

                                    <td>
                                        {Math.round(Number(farm.progressLastCyclePercentage) || 0)}
                                        %
                                    </td>

                                    <td>
                                        <FarmStatusBadge
                                            $color={
                                                farm.statusColor
                                            }
                                        >
                                            {farm.statusLabel ||
                                                "Sin estado"}
                                        </FarmStatusBadge>
                                    </td>

                                    <td>
                                        {formatNumber(
                                            farm.pendingReceptionQuantity
                                        )}
                                    </td>

                                </tr>
                            ))}
                        </tbody>

                        <tfoot>
                            <tr>
                                <td>
                                    Totales
                                </td>

                                <td>
                                    {formatNumber(
                                        farmsTableTotals.totalNetArea
                                    )}{" "}
                                    ha
                                </td>

                                <td>
                                    {formatNumber(
                                        farmsTableTotals.totalLots
                                    )}
                                </td>

                                <td>
                                    —
                                </td>

                                <td>
                                    —
                                </td>

                                <td>
                                    {farmsTableTotals.averageBultosPerHectare || "—"}
                                </td>

                                <td>
                                    {formatNumber(
                                        farmsTableTotals.expectedLastCycleQuantity
                                    )}
                                </td>

                                <td>
                                    {formatNumber(
                                        farmsTableTotals.receivedLastCycleQuantity
                                    )}
                                </td>

                                <td>
                                    {formatNumber(
                                        farmsTableTotals.totalAppliedQuantity
                                    )}
                                </td>

                                <td>
                                    {formatNumber(
                                        farmsTableTotals.pendingApplyFromReceivedQuantity
                                    )}
                                </td>

                                <td>
                                    {formatNumber(
                                        farmsTableTotals.transportTripsCount
                                    )}
                                </td>

                                <td>
                                    {formatNumber(
                                        farmsTableTotals.applicationDaysCount
                                    )}
                                </td>

                                <td>
                                    {formatNumber(
                                        farmsTableTotals.transportedPeopleQuantity
                                    )}
                                </td>

                                <td>
                                    {formatNumber(
                                        farmsTableTotals.workerDaysCount
                                    )}
                                </td>

                                <td>
                                    {farmsTableTotals.averageBagsPerWorkerDay || "—"}
                                </td>

                                <td>
                                    —
                                </td>

                                <td>
                                    —
                                </td>

                                <td>
                                    —
                                </td>

                                <td>
                                    —
                                </td>

                                {tableCycles.map((cycleNumber) => (
                                    <td key={`total-cycle-${cycleNumber}`}>
                                        —
                                    </td>
                                ))}

                                <td>
                                    —
                                </td>

                                <td>
                                    —
                                </td>

                                <td>
                                    {formatNumber(
                                        farmsTableTotals.pendingReceptionQuantity
                                    )}
                                </td>
                            </tr>
                        </tfoot>

                    </FarmsDynamicTable>
                </FarmsLeftPanel>
            </FarmsSplitContent>

        </Page>
    );
};